package com.siriusxm.unifiedcontent.console.ucp

import com.siriusxm.pia.ApplicationContext
import com.siriusxm.service.registry.ConditionCombinator
import com.siriusxm.service.registry.ConditionExpression
import com.siriusxm.service.registry.ConditionExpressionType
import com.siriusxm.service.registry.ConditionGroup
import com.siriusxm.service.registry.Policy
import com.siriusxm.service.registry.PolicyContextCondition
import com.siriusxm.service.registry.PolicyContextConditionOperator
import com.siriusxm.service.registry.PolicyInput
import com.siriusxm.service.registry.ServiceRegistry
import com.siriusxm.service.registry.SubjectType
import io.ktor.client.HttpClient
import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
import io.ktor.serialization.kotlinx.json.json
import kotlinx.serialization.json.Json

/**
 * The aggregator actions a consumer's team needs to run its own targets (UCA-1240): administer them,
 * and backfill and replay to them. Each is granted by its own policy so any one can be revoked alone.
 */
val CONSUMER_TEAM_ACTIONS: List<String> = listOf(
    "UnifiedContentAggregator_ConsumerTargetEdit",
    "UnifiedContentAggregator_Backfill",
    "UnifiedContentAggregator_Replay",
)

/**
 * The UCP policy that grants a consumer's team one aggregator action, scoped the way the aggregator
 * scopes the check: `context.consumerId == <consumer> && context.targetAccountId in <accounts>`.
 *
 * The description carries a marker naming the consumer, so a later sync finds and updates the same
 * policy rather than adding a second one. A team can own several consumers, and each gets its own
 * policies.
 */
data class DesiredConsumerPolicy(
    val actionId: String,
    val consumerId: String,
    val accountIds: List<String>,
) {
    val description: String
        get() = "${syncMarker(consumerId)} Lets the consumer's team use $actionId for consumer $consumerId " +
                "in accounts ${accountIds.joinToString(", ")}. Managed by Sync Permissions in the CMC."

    val expression: ConditionExpression
        get() = ConditionExpression(
            type = ConditionExpressionType.GROUP,
            group = ConditionGroup(
                combinator = ConditionCombinator.AND,
                expressions = listOf(
                    leaf(PolicyContextCondition("consumerId", PolicyContextConditionOperator.EQUALS, value = consumerId)),
                    leaf(PolicyContextCondition("targetAccountId", PolicyContextConditionOperator.IN, values = accountIds)),
                )
            )
        )

    fun input(teamId: String): PolicyInput = PolicyInput(
        subjectType = SubjectType.TEAM,
        subjectId = teamId,
        actionId = actionId,
        description = description,
        contextExpression = expression,
    )

    private fun leaf(condition: PolicyContextCondition) =
        ConditionExpression(type = ConditionExpressionType.LEAF, leaf = condition)
}

/** The description prefix that marks a policy as owned by the sync for [consumerId]. */
fun syncMarker(consumerId: String): String = "[cmc-consumer-sync:$consumerId]"

/**
 * The policies a team should hold for a consumer, one per [CONSUMER_TEAM_ACTIONS] entry, or null when
 * the consumer has no AWS accounts. An account filter with no accounts would match nothing, and
 * dropping the filter would grant every account, so the sync refuses instead.
 */
fun desiredConsumerPolicies(consumerId: String, accountIds: Collection<String>): List<DesiredConsumerPolicy>? {
    val accounts = accountIds.map { it.trim() }.filter { it.isNotEmpty() }.distinct().sorted()
    if (accounts.isEmpty()) return null
    return CONSUMER_TEAM_ACTIONS.map { DesiredConsumerPolicy(it, consumerId, accounts) }
}

/** What a sync will do, or did, for one action. */
enum class PolicySyncChange { CREATE, UPDATE, UNCHANGED }

/** One action's planned or applied change. */
data class PolicySyncStep(val desired: DesiredConsumerPolicy, val existing: Policy?, val change: PolicySyncChange)

/**
 * Compare the desired policies with the team's existing ones for the same actions. A policy is the
 * sync's own when its description starts with the consumer's [syncMarker]; hand-written grants for the
 * same action are left alone, so the sync never narrows or removes access someone granted on purpose.
 */
fun planConsumerPolicySync(desired: List<DesiredConsumerPolicy>, existing: Collection<Policy>): List<PolicySyncStep> =
    desired.map { want ->
        val mine = existing.firstOrNull {
            it.actionId == want.actionId && it.description?.startsWith(syncMarker(want.consumerId)) == true
        }
        val change = when {
            mine == null -> PolicySyncChange.CREATE
            mine.contextExpression == want.expression && mine.description == want.description -> PolicySyncChange.UNCHANGED
            else -> PolicySyncChange.UPDATE
        }
        PolicySyncStep(want, mine, change)
    }

/**
 * Creates and updates a consumer team's aggregator policies in Unified Content Permissions with the
 * viewer's token (UCA-1240). UCP authorizes every write itself (ManagePolicies); the console only
 * offers the action to viewers AVP says may manage policies.
 *
 * @param baseUrl the UCP API base URL from `ucpApiUrl` in config.json, or null when not configured
 */
class ConsumerPolicySyncClient(private val context: ApplicationContext, private val baseUrl: String?) {

    private val api: ServiceRegistry? by lazy {
        val url = baseUrl?.takeIf { it.isNotBlank() } ?: return@lazy null
        ServiceRegistry(
            url,
            HttpClient {
                install(ContentNegotiation) {
                    json(Json {
                        isLenient = true
                        ignoreUnknownKeys = true
                    })
                }
                context.installAuth(this)
            }
        )
    }

    private fun client(): ServiceRegistry =
        api ?: throw IllegalStateException("UCP is not configured for this environment.")

    /** The UCP id of the service that owns the aggregator actions, for the ManagePolicies check. */
    suspend fun aggregatorServiceId(): String = client().getService(AGGREGATOR_SERVICE_ID).id

    /** Plan the sync for [teamId] (the team's UCP id) without changing anything. */
    suspend fun plan(teamId: String, desired: List<DesiredConsumerPolicy>): List<PolicySyncStep> {
        val existing = desired.map { it.actionId }.distinct().flatMap { actionId ->
            client().listPolicies(subjectType = SubjectType.TEAM, subjectId = teamId, actionId = actionId)
        }
        return planConsumerPolicySync(desired, existing)
    }

    /** Apply [steps] from [plan]. Each write is independent, so a failure leaves earlier ones in place. */
    suspend fun apply(teamId: String, steps: List<PolicySyncStep>) {
        steps.forEach { step ->
            when (step.change) {
                PolicySyncChange.CREATE -> client().createPolicy(step.desired.input(teamId))
                PolicySyncChange.UPDATE -> client().updatePolicy(step.existing!!.id, step.desired.input(teamId))
                PolicySyncChange.UNCHANGED -> Unit
            }
        }
    }

    companion object {
        /** The aggregator's UCP service `friendlyId`; its actions are `UnifiedContentAggregator_*`. */
        const val AGGREGATOR_SERVICE_ID: String = "unified-content-aggregator"
    }
}
